"use client";

import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  applyThemeClass,
  getEffectiveTheme,
  getStoredThemePreference,
  getThemeFromLocalTime,
  msUntilNextAutoThemeChange,
  persistThemePreference,
  type Theme,
  type ThemePreference,
} from "@/lib/theme";

type ThemeContextValue = {
  theme: Theme;
  preference: ThemePreference;
  setTheme: (next: Theme) => void;
  toggleTheme: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreference] = useState<ThemePreference>("auto");
  const [theme, setThemeState] = useState<Theme>(() => getThemeFromLocalTime());
  const [mounted, setMounted] = useState(false);

  const syncTheme = useCallback((nextPreference: ThemePreference) => {
    const effective = getEffectiveTheme(nextPreference);
    setPreference(nextPreference);
    setThemeState(effective);
    applyThemeClass(effective);
  }, []);

  useEffect(() => {
    const stored = getStoredThemePreference();
    syncTheme(stored);
    setMounted(true);
  }, [syncTheme]);

  // Re-apply automatic light/dark when local daylight boundaries are crossed.
  useEffect(() => {
    if (!mounted || preference !== "auto") return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleNext = () => {
      timeoutId = setTimeout(() => {
        syncTheme("auto");
        scheduleNext();
      }, msUntilNextAutoThemeChange());
    };

    scheduleNext();

    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, [mounted, preference, syncTheme]);

  const setTheme = useCallback(
    (next: Theme) => {
      persistThemePreference(next);
      syncTheme(next);
    },
    [syncTheme]
  );

  const toggleTheme = useCallback(() => {
    setTheme(theme === "light" ? "dark" : "light");
  }, [setTheme, theme]);

  const value: ThemeContextValue = {
    theme: mounted ? theme : getThemeFromLocalTime(),
    preference: mounted ? preference : "auto",
    setTheme,
    toggleTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
